import { ApiError, explain } from '@/shared/api/problem'
import type {
  SurveyBundleDto, SurveyQuestionDto, SurveyQuestionOptionDto, SurveyResponseDetailDto,
} from '@/shared/api/types'
import { captureLocation, type LocationOutcome } from '@/shared/geo/geolocation'
import { evaluateSkips, type AnswerMap } from './skipRules'
import { getBundle, getRegisterVocabulary, myAssignments, submitResponse } from './api'
import {
  cacheBundle, cachedBundle, clearDraft, drainQueue, holdClipsForUpload, loadDraft,
  queueInterview, saveDraft, type CaptureDraft,
} from './captureQueue'
import {
  cancelRecording, hasClip, resetRecorder, startRecording, stopRecording, takeUnstoredClips,
  uploadHeldClips, getRecorderSnapshot,
} from './AudioRecorder'

/**
 * One interview, living OUTSIDE React.
 *
 * ## Why a module singleton and not component state
 *
 * The same reason `coverage/broadcastSession.ts` is one: this session owns a
 * microphone. That module exists because a live camera was held in a
 * `useEffect`, and navigating away ran the cleanup and took the broadcast off
 * air while the panel still said "live". An interview that loses its audio
 * because the interviewer glanced at the quota screen is the identical bug, and
 * here the cost is a respondent's twenty minutes that cannot be asked for again.
 *
 * So the whole interview — the key, the clock, the answers, the consent — lives
 * at module scope and React subscribes through `useSyncExternalStore`. Unmounting
 * `CapturePage` does not end an interview. Exactly three things do: submitting
 * it, starting a different one, and closing the tab.
 *
 * ## The idempotency key is the point of this file
 *
 * It is generated ONCE, when the interview starts, written to `localStorage`
 * immediately, and never regenerated — not on a failed submit, not on a reload,
 * not on a retry. A device on a flaky connection cannot tell a lost response
 * from a lost request; the only safe answer to that ambiguity is for the retry
 * to carry the same key, so the server can hand back the interview it already
 * has instead of creating a second respondent who never existed. Everything else
 * on this screen is recoverable. That is not.
 *
 * ## Two skip evaluators, and which one is authoritative
 *
 * `skipRules.ts` is imported, never reimplemented. It drives WHICH QUESTION IS
 * SHOWN NEXT, because that decision has to be made between one question and the
 * next with no round trip. It does NOT decide what is recorded: the submission
 * carries an answer row only for questions the respondent actually answered, and
 * the server replays its own evaluator to mark the rest as either branched-past
 * (`skippedByRuleId`) or shown-and-left-blank. If the two evaluators ever
 * disagree the server wins, and the interview is still recorded correctly.
 */

export type CapturePhase =
  | 'idle'
  | 'loading'
  | 'unavailable'
  | 'consent'
  | 'interview'
  | 'details'
  | 'submitting'
  | 'done'

/** What one question is holding. Absent keys are "not answered", not "answered null". */
export interface DraftAnswer {
  optionCodes?: string[]
  number?: string
  bool?: boolean
  text?: string
  otherText?: string
}

export interface RespondentDetails {
  name: string
  /** E.164 when the picker has a complete number, '' otherwise. Never national format. */
  phone: string
  /**
   * ISO 3166-1 alpha-2 for {@link phone}.
   *
   * `ResponseSubmission.respondentPhoneCountry` has existed on the server since the phone
   * work, and the capture screen never sent it — so every interview phone captured from the
   * console went through the legacy path that normalises without VALIDATING. A number is
   * only checkable against a numbering plan if you know the plan.
   */
  phoneCountry: string
  gender: string
  age: string
  unitId: string
  unitName: string | null
  /** The ID typed at the doorstep. Sealed server-side; never persisted here. */
  nationalId: string
  /**
   * Where the three fields above came from.
   *
   * `MANUAL` until a lookup fills them, and back to `MANUAL` the moment somebody
   * edits one — a field the interviewer overwrote is no longer what the register
   * said, and recording it as though it were would make a keystroke look like a
   * citation.
   */
  identitySourceCode: 'MANUAL' | 'LOCAL_REGISTER' | 'ECITIZEN_IPRS'
  /** Echoed back on submit so the interview links to the register row. */
  matchedVoterId: string | null
}

export interface SubmitResult {
  responseId: string
  detail: SurveyResponseDetailDto
  /** True when this submission was a RETRY of one that may already have landed. */
  retried: boolean
  dispositionCode: string
}

export interface CaptureSnapshot {
  phase: CapturePhase
  surveyId: string | null
  assignmentId: string | null
  bundle: SurveyBundleDto | null
  /** True when the instrument came out of `localStorage` rather than the network. */
  fromCache: boolean
  cachedAt: string | null
  loadError: string | null
  /** The server's sentence when it refused a submission. Rendered, never paraphrased. */
  refusal: string | null
  refusalCode: string | null
  register: Record<string, string[]>

  idempotencyKey: string
  languageCode: string
  optionOrderSeed: number
  startedAt: string | null

  consentRequired: boolean
  consentGiven: boolean | null
  consentAt: string | null
  consentMethodCode: string

  answers: Record<string, DraftAnswer>
  /** Index into the visible question list. */
  cursor: number

  details: RespondentDetails
  place: LocationOutcome | null
  geoState: 'idle' | 'asking' | 'settled'

  result: SubmitResult | null
  /** True when the interview went into the offline queue instead of being sent. */
  queued: boolean
  submitting: boolean
  /** Bumped every time a submit is attempted; > 1 means the key is being reused. */
  attempts: number
}

const BLANK_DETAILS: RespondentDetails = {
  name: '', phone: '', phoneCountry: 'KE', gender: '', age: '', unitId: '', unitName: null,
  nationalId: '', identitySourceCode: 'MANUAL', matchedVoterId: null,
}

function blank(): CaptureSnapshot {
  return {
    phase: 'idle',
    surveyId: null,
    assignmentId: null,
    bundle: null,
    fromCache: false,
    cachedAt: null,
    loadError: null,
    refusal: null,
    refusalCode: null,
    register: {},
    idempotencyKey: '',
    languageCode: 'en',
    optionOrderSeed: newSeed(),
    startedAt: null,
    consentRequired: false,
    consentGiven: null,
    consentAt: null,
    consentMethodCode: 'VERBAL_ATTESTED',
    answers: {},
    cursor: 0,
    details: { ...BLANK_DETAILS },
    place: null,
    geoState: 'idle',
    result: null,
    queued: false,
    submitting: false,
    attempts: 0,
  }
}

let snapshot: CaptureSnapshot = blank()
const listeners = new Set<() => void>()
/** Bumped by every fresh interview — invalidates an in-flight load or submit. */
let run = 0

function set(patch: Partial<CaptureSnapshot>) {
  snapshot = { ...snapshot, ...patch }
  listeners.forEach((l) => l())
  persist()
}

export function subscribeCapture(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function getCaptureSnapshot(): CaptureSnapshot {
  return snapshot
}

// ── persistence ─────────────────────────────────────────────────────────────

/**
 * Write the interview to disk on every change.
 *
 * Cheap, and the alternative is choosing which changes matter — which is how the
 * key survives but the answers do not, or the reverse. A reload during an
 * interview must come back to the same interview or it is a new respondent.
 */
function persist() {
  if (!snapshot.surveyId || !snapshot.idempotencyKey) return
  if (snapshot.phase === 'done' || snapshot.phase === 'idle') return
  const draft: CaptureDraft = {
    idempotencyKey: snapshot.idempotencyKey,
    startedAt: snapshot.startedAt,
    languageCode: snapshot.languageCode,
    optionOrderSeed: snapshot.optionOrderSeed,
    consentGiven: snapshot.consentGiven,
    consentAt: snapshot.consentAt,
    answers: snapshot.answers,
    details: snapshot.details as unknown as Record<string, unknown>,
  }
  saveDraft(snapshot.surveyId, snapshot.assignmentId, draft)
}

function newKey(): string {
  return typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    // A device without `randomUUID` still needs a key that will not collide with
    // another interviewer's. Time plus two random words is not a UUID, but it is
    // unique enough for a 64-character field scoped to one poll.
    : `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

/** A positive 31-bit int — `optionOrderSeed` is an `Integer` on the wire. */
function newSeed(): number {
  return Math.floor(Math.random() * 0x7fffffff)
}

// ── opening a poll ──────────────────────────────────────────────────────────

/**
 * Load the bundle and resume, or start, an interview.
 *
 * Cache-first-on-failure rather than cache-first: a device with signal should
 * ask, because a poll that has moved to PAUSED or published a new instrument
 * version is exactly the thing a cached bundle would hide. A device without
 * signal falls back and says which it is looking at.
 */
export async function openCapture(surveyId: string, assignmentId: string | null): Promise<void> {
  if (snapshot.surveyId === surveyId
    && snapshot.assignmentId === assignmentId
    && snapshot.phase !== 'idle'
    && snapshot.phase !== 'unavailable') return

  const mine = ++run
  set({
    ...blank(), phase: 'loading', surveyId, assignmentId,
  })

  let bundle: SurveyBundleDto | null = null
  let fromCache = false
  let cachedAt: string | null = null
  let loadError: string | null = null

  try {
    bundle = await getBundle(surveyId)
    cacheBundle(surveyId, bundle)
  } catch (e) {
    const cached = cachedBundle(surveyId)
    if (cached) {
      bundle = cached.bundle
      fromCache = true
      cachedAt = cached.cachedAt
      loadError = null
    } else {
      loadError = explain(e)
    }
  }
  if (run !== mine) return
  if (!bundle) {
    set({ phase: 'unavailable', loadError })
    return
  }

  // Both of these are ordinary reads that must not stop an interview: a device
  // with a cached bundle and no signal still conducts the interview, it just
  // shows fewer labels while doing it.
  const [register, unit] = await Promise.all([
    getRegisterVocabulary().catch(() => ({} as Record<string, string[]>)),
    resolveUnit(surveyId, assignmentId, bundle),
  ])
  if (run !== mine) return

  const draft = loadDraft(surveyId, assignmentId)
  const languages = bundle.instrument.languages
  const consentRequired = bundle.instrument.consentRequired

  const resumed = draft !== null
  const details = resumed
    ? { ...BLANK_DETAILS, ...(draft.details as unknown as RespondentDetails), ...unit }
    : { ...BLANK_DETAILS, ...unit }

  set({
    phase: 'loading',
    bundle,
    fromCache,
    cachedAt,
    loadError: null,
    register,
    // THE KEY IS REUSED WHEN A DRAFT EXISTS. A reload mid-interview that minted a
    // new one would submit the same respondent twice the moment both attempts
    // reached the server.
    idempotencyKey: draft?.idempotencyKey ?? newKey(),
    languageCode: draft?.languageCode ?? languages[0] ?? 'en',
    optionOrderSeed: draft?.optionOrderSeed ?? newSeed(),
    startedAt: draft?.startedAt ?? null,
    consentRequired,
    consentGiven: draft?.consentGiven ?? null,
    consentAt: draft?.consentAt ?? null,
    answers: (draft?.answers as Record<string, DraftAnswer> | undefined) ?? {},
    details,
    cursor: 0,
  })

  // A resumed interview that already had consent goes straight back to the
  // questions; a fresh one starts where the poll says it starts.
  if (consentRequired && snapshot.consentGiven !== true) set({ phase: 'consent' })
  else beginQuestions()

  /*
    THE RECORDING STARTS WHEN THE INTERVIEW OPENS.

    It used to start at consent, with the comment "not one moment earlier". That
    was the safer default and this is a deliberate change of it: the approach and
    the consent statement itself are now inside the recording, which is what a
    back-check listening for a coerced or skipped consent actually needs to hear.

    Two things carry the weight of that change, and neither is optional:

      * `refuseConsent` calls `resetRecorder()`, which DISCARDS every clip
        including this one. Somebody who declined is never uploaded.
      * the consent script must say recording has already begun. The server
        already refuses to let a poll with audio go live without an audio
        disclosure in every language of its consent script; that guard is what
        makes this honest rather than merely earlier.
  */
  if ((snapshot.bundle?.instrument.audioRecordingMode ?? 'OFF') !== 'OFF') {
    await startRecording('AUDIO_INTERVIEW')
  }
}

/**
 * Which unit does this interview belong to?
 *
 * `unitId` is `@NotNull` on the submission and there is no unit picker in
 * `polling/api.ts` — nor should there be one here: an interviewer works the
 * area they were assigned, and letting them retype it is how an interview ends
 * up attributed to a ward nobody sent them to. So it comes from the assignment
 * when there is one and from the poll's scope when there is not, and the screen
 * shows which.
 */
async function resolveUnit(
  surveyId: string, assignmentId: string | null, bundle: SurveyBundleDto,
): Promise<{ unitId: string; unitName: string | null }> {
  const fallback = { unitId: bundle.scopeUnitId, unitName: bundle.scopeUnitName }
  if (!assignmentId) return fallback
  try {
    const mine = await myAssignments()
    const found = mine.find((a) => a.id === assignmentId && a.surveyId === surveyId)
    if (found?.scopeUnitId) return { unitId: found.scopeUnitId, unitName: found.scopeUnitName }
  } catch {
    // Offline, or the roster is not readable. The poll's scope is a true answer
    // and a narrower one is not available, so the interview is not blocked.
  }
  return fallback
}

// ── consent ─────────────────────────────────────────────────────────────────

export function setLanguage(languageCode: string): void {
  set({ languageCode })
}

export function setConsentMethod(consentMethodCode: string): void {
  set({ consentMethodCode })
}

/**
 * Consent given. The recording starts HERE and not one moment earlier.
 *
 * `VERBAL_RECORDED` produces a **separate** `AUDIO_CONSENT` clip before the
 * interview clip. Two files, not one, so a verification can confirm the consent
 * was obtained without anybody listening to what the respondent then said —
 * those are different disclosures and the second one needs `survey:listen`.
 */
export async function giveConsent(): Promise<void> {
  set({ consentGiven: true, consentAt: new Date().toISOString(), refusal: null })
  beginQuestions()
  const mode = snapshot.bundle?.instrument.audioRecordingMode ?? 'OFF'
  if (mode !== 'OFF' && snapshot.consentMethodCode !== 'VERBAL_RECORDED') {
    await startRecording('AUDIO_INTERVIEW')
  }
}

/**
 * The consent statement, recorded as its OWN clip.
 *
 * <p>Now that the interview clip starts when capture opens, this has to
 * interrupt it — `startRecording` is a no-op while something is already
 * recording, so without the interruption below the consent statement would land
 * inside the interview clip and the separate file would silently never
 * appear.</p>
 *
 * <p>The separation is the point: a verifier can confirm consent was obtained
 * without hearing what the respondent then said, and hearing that needs
 * `survey:listen`.</p>
 *
 * ## `cancelRecording`, not `stopRecording`, and the index that decides it
 *
 * `ux_survey_response_media__interview` is UNIQUE on `(response_id, kind)` for
 * whole-interview media: **one** AUDIO_INTERVIEW row per interview, because a
 * second one would double every duration-based integrity check.
 *
 * Stopping here KEPT the pre-consent fragment as a clip, and a fresh interview
 * clip then began after the consent statement — two AUDIO_INTERVIEW clips for
 * one response. The first uploaded, the second hit the unique index, and the
 * interviewer saw "Upload failed" for a recording that was never storable.
 *
 * So the run-up is discarded on this path. Nothing is lost that is not kept
 * somewhere better: this branch runs only for `VERBAL_RECORDED`, where the
 * consent clip that starts immediately IS the record of the approach and the
 * agreement, which is what a back-check listens for.
 */
export async function startConsentRecording(): Promise<void> {
  cancelRecording()
  await startRecording('AUDIO_CONSENT')
}

export async function stopConsentRecording(): Promise<void> {
  await stopRecording()
  if ((snapshot.bundle?.instrument.audioRecordingMode ?? 'OFF') !== 'OFF') {
    await startRecording('AUDIO_INTERVIEW')
  }
}

/**
 * Consent refused.
 *
 * A refusal is DATA. It is the denominator of the cooperation rate and it is
 * what tells a supervisor that a ward is hostile rather than unvisited. So the
 * interview ends here and is submitted with `dispositionCode: 'REFUSAL'` and
 * `consentGiven: false` — not abandoned, not left as a half-filled form nobody
 * ever sees.
 *
 * The server may still refuse to store it: a poll with `consentRequired` answers
 * `survey/consent-required` and directs the interviewer to log a REFUSAL contact
 * attempt instead, which counts in the same rates. That sentence is rendered as
 * given, and `CaptureSummary` offers the action it names.
 */
export async function refuseConsent(): Promise<void> {
  // resetRecorder, not cancelRecording: the recording now starts when capture
  // opens, so by this point there may be a FINISHED clip of the approach as well
  // as a running one. Cancelling only the running one would upload audio of
  // somebody who has just declined to be interviewed.
  resetRecorder()
  set({ consentGiven: false, consentAt: new Date().toISOString() })
  await submit('REFUSAL')
}

function beginQuestions(): void {
  set({
    phase: 'interview',
    // `startedAt` is set when the FIRST question is shown and is sent exactly as
    // recorded. The server derives its own duration from receipt and compares
    // the two; a disagreement over 25% raises DURATION_DISAGREEMENT. Backdating
    // this to make an interview look longer, or forward-dating it to hide a
    // speeder, is the fabrication that rule exists to catch.
    startedAt: snapshot.startedAt ?? new Date().toISOString(),
  })
  if (snapshot.geoState === 'idle') void locate()
}

// ── location ────────────────────────────────────────────────────────────────

/**
 * Where the interview happened.
 *
 * `captureLocation` is `features/coverage`'s, imported rather than copied: it
 * already asks with `{enableHighAccuracy: true, timeout: 20000, maximumAge:
 * 60000}` — the exact options this screen needs — and a second copy of a
 * browser-permission wrapper is a second thing to keep in step.
 *
 * A refused or unavailable fix DOES NOT BLOCK the submission. The server raises
 * `GPS_MISSING`, which is the correct outcome: an interview with an honest
 * "no fix" is reviewable, and the pressure created by making a fix mandatory is
 * somebody typing in a plausible coordinate, which is worse than none.
 *
 * Note the browser only offers geolocation on a **secure origin**. `https://…`
 * and `http://localhost` work; a bare LAN address like `http://192.168.1.20`
 * does not, and `navigator.geolocation` is simply absent there rather than
 * erroring in a way that names the cause. This has already confused somebody on
 * this project.
 */
export async function locate(): Promise<void> {
  set({ geoState: 'asking' })
  const place = await captureLocation()
  set({ place, geoState: 'settled' })
}

// ── answering ───────────────────────────────────────────────────────────────

export function answer(questionId: string, patch: DraftAnswer | null): void {
  const next = { ...snapshot.answers }
  if (patch === null) delete next[questionId]
  else next[questionId] = patch
  set({ answers: next, refusal: null })
}

export function goToCursor(cursor: number): void {
  set({ cursor: Math.max(0, cursor) })
}

export function nextQuestion(): void {
  const visible = visibleQuestions()
  if (snapshot.cursor + 1 >= visible.length) set({ phase: 'details' })
  else set({ cursor: snapshot.cursor + 1 })
}

export function previousQuestion(): void {
  if (snapshot.cursor === 0) {
    if (snapshot.consentRequired) set({ phase: 'consent' })
    return
  }
  set({ cursor: snapshot.cursor - 1 })
}

export function backToQuestions(): void {
  const visible = visibleQuestions()
  set({ phase: 'interview', cursor: Math.max(0, visible.length - 1) })
}

/**
 * Editing a prefilled field takes the interview back to MANUAL.
 *
 * The source is a claim about where the name, gender and age came from. Once an
 * interviewer has overwritten one of them it is no longer what the register
 * said, and leaving the row stamped `LOCAL_REGISTER` would make a keystroke look
 * like a citation to every analysis that reads it.
 *
 * The PHONE is exempt: it is expected to be corrected — an eCitizen number is
 * often out of date and the whole point of prefilling it is that the interviewer
 * confirms it — and correcting it says nothing about where the identity came
 * from. So is `nationalId` itself, which is what produced the match.
 */
const IDENTITY_FIELDS: (keyof RespondentDetails)[] = ['name', 'gender', 'age']

export function setDetails(patch: Partial<RespondentDetails>): void {
  const touchedIdentity = IDENTITY_FIELDS.some(
    (field) => field in patch && patch[field] !== snapshot.details[field])
  const next: RespondentDetails = { ...snapshot.details, ...patch }
  if (touchedIdentity && next.identitySourceCode !== 'MANUAL') {
    next.identitySourceCode = 'MANUAL'
    next.matchedVoterId = null
  }
  set({ details: next, refusal: null })
}

/**
 * Apply what a lookup returned.
 *
 * Separate from {@link setDetails} precisely because that one downgrades the
 * source on edit — routing a prefill through it would clear the source it is
 * setting. Fields the lookup did not answer are LEFT ALONE rather than blanked:
 * a register match carries no phone, and wiping a number the interviewer had
 * already written down would be the prefill destroying data.
 */
export function applyIdentity(match: {
  sourceCode: string | null
  voterId: string | null
  displayName: string | null
  genderCode: string | null
  birthYear: number | null
  mobileNumber: string | null
}): void {
  const source = match.sourceCode === 'LOCAL_REGISTER' || match.sourceCode === 'ECITIZEN_IPRS'
    ? match.sourceCode : 'MANUAL'
  const details: RespondentDetails = {
    ...snapshot.details,
    identitySourceCode: source,
    matchedVoterId: source === 'LOCAL_REGISTER' ? match.voterId : null,
  }
  if (match.displayName) details.name = match.displayName
  if (match.genderCode) details.gender = match.genderCode
  if (match.birthYear) {
    // The YEAR is what the registers hold; the field asks for years of age. One
    // conversion, here, rather than a birth year typed into an age box — which
    // is the mistake that produced a 500 from a database constraint.
    details.age = String(new Date().getFullYear() - match.birthYear)
  }
  if (match.mobileNumber) details.phone = match.mobileNumber
  set({ details, refusal: null })
}

// ── the question walk ───────────────────────────────────────────────────────

/**
 * A MATRIX is a GROUP, not a walk of its own.
 *
 * Its sub-questions carry `parentQuestionId` and appear in the same flat list, so
 * without this they would each be asked as a separate screen and the respondent
 * would hear "on the same scale…" fourteen times. They are pulled out of the
 * top-level walk and rendered as rows under the parent, which is how a matrix is
 * administered on paper and how the analysis expects to read it back.
 */
export function isSubQuestion(q: SurveyQuestionDto): boolean {
  return q.parentQuestionId !== null && q.parentQuestionId !== undefined
}

export function subQuestions(
  all: SurveyQuestionDto[], parent: SurveyQuestionDto,
): SurveyQuestionDto[] {
  return all
    .filter((q) => q.parentQuestionId === parent.id)
    .sort((a, b) => a.position - b.position)
}

/**
 * The answer map the skip evaluator reads: question id → one option CODE.
 *
 * Codes, never ids or labels — the server compares on the code, ids are
 * per-version and labels are editable. A multi-select contributes its first
 * selected code, which is what `skipRules.ts` documents its own limits around;
 * the server re-evaluates the full set on submit and its answer is the one that
 * is stored.
 */
function skipAnswers(questions: SurveyQuestionDto[]): AnswerMap {
  const map: AnswerMap = {}
  for (const q of questions) {
    const a = snapshot.answers[q.id]
    if (!a) continue
    const first = a.optionCodes?.[0]
    if (first) { map[q.id] = first; continue }
    if (a.bool !== undefined) { map[q.id] = a.bool ? 'YES' : 'NO'; continue }
    if (a.number !== undefined && a.number !== '') { map[q.id] = a.number; continue }
    if (a.text) map[q.id] = a.text
  }
  return map
}

export interface Walk {
  visible: SurveyQuestionDto[]
  hiddenBy: Record<string, string>
  ended: boolean
  all: SurveyQuestionDto[]
}

/**
 * Which questions this respondent sees, right now.
 *
 * Question order is the instrument's `position` and is NOT randomised even when
 * the poll declares `randomiseQuestionOrder`. Branching is defined over position
 * — a `SKIP_TO` hides everything strictly between the source and the target —
 * so shuffling the order would change which questions a branch passes over, and
 * the server's own evaluator, which reads positions, would then disagree with
 * what was actually asked. Option order is randomised instead, and that is the
 * one whose seed is recorded.
 */
export function walk(): Walk {
  const bundle = snapshot.bundle
  if (!bundle) return { visible: [], hiddenBy: {}, ended: false, all: [] }
  const all = [...bundle.instrument.questions].sort((a, b) => a.position - b.position)
  const top = all.filter((q) => !isSubQuestion(q))
  const outcome = evaluateSkips(all, bundle.instrument.skipRules, skipAnswers(all))
  return {
    visible: top.filter((q) => !outcome.hiddenBy[q.id]),
    hiddenBy: outcome.hiddenBy,
    ended: outcome.ended,
    all,
  }
}

export function visibleQuestions(): SurveyQuestionDto[] {
  return walk().visible
}

// ── option order ────────────────────────────────────────────────────────────

/** xorshift-ish 32-bit PRNG. Deterministic, so the seed reproduces the order. */
function mulberry32(a: number) {
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function hash(seed: number, salt: string): number {
  let h = seed >>> 0
  for (let i = 0; i < salt.length; i += 1) {
    h = Math.imul(h ^ salt.charCodeAt(i), 16777619) >>> 0
  }
  return h
}

/**
 * The order the options are actually shown in, and why the seed is on the wire.
 *
 * Order effects are real and measurable — the first substantive option in a list
 * picks up several points that belong to nobody — and they are only analysable
 * AFTERWARDS if the order each respondent saw can be reconstructed. So the seed
 * is generated once per interview, used here, and sent as `optionOrderSeed`.
 * Shuffling without recording the seed would be worse than not shuffling: it
 * introduces the variance and destroys the ability to model it.
 *
 * Three groups never move. Anchored options (`anchorCode`) are anchored because
 * an author said so — "None of the above" in the middle of a list is a misread
 * question. `OTHER_SPECIFY` follows the substantive block. `DONT_KNOW`,
 * `REFUSED` and `NOT_APPLICABLE` come last, always, because they sit outside the
 * base and a respondent scanning for a real answer should not have to read past
 * them.
 */
export function orderedOptions(
  question: SurveyQuestionDto, seed: number, randomiseGlobally: boolean,
): SurveyQuestionOptionDto[] {
  const options = [...question.options].sort((a, b) => a.position - b.position)
  const outside = options.filter((o) => NON_SUBSTANTIVE.has(o.kindCode))
  const other = options.filter((o) => o.kindCode === 'OTHER_SPECIFY')
  const core = options.filter(
    (o) => !NON_SUBSTANTIVE.has(o.kindCode) && o.kindCode !== 'OTHER_SPECIFY',
  )
  const top = core.filter((o) => o.anchorCode === 'TOP')
  const bottom = core.filter((o) => o.anchorCode === 'BOTTOM')
  const middle = core.filter((o) => o.anchorCode !== 'TOP' && o.anchorCode !== 'BOTTOM')

  if (randomiseGlobally && question.randomiseOptions && middle.length > 1) {
    // Salted per question so two questions with the same options in the same
    // interview do not get the same permutation — which would look like an order
    // effect that is really a seeding artefact.
    const rand = mulberry32(hash(seed, question.code))
    for (let i = middle.length - 1; i > 0; i -= 1) {
      const j = Math.floor(rand() * (i + 1))
      const a = middle[i]!
      const b = middle[j]!
      middle[i] = b
      middle[j] = a
    }
  }
  return [...top, ...middle, ...bottom, ...other, ...outside]
}

const NON_SUBSTANTIVE = new Set(['DONT_KNOW', 'REFUSED', 'NOT_APPLICABLE'])

export function isOutsideBase(option: SurveyQuestionOptionDto): boolean {
  return NON_SUBSTANTIVE.has(option.kindCode)
}

// ── translation ─────────────────────────────────────────────────────────────

/** The question in the chosen language, falling back to the authored text. */
export function questionText(q: SurveyQuestionDto, language: string): string {
  return q.translations?.[language]?.text ?? q.text
}

export function questionHelp(q: SurveyQuestionDto, language: string): string | null {
  return q.translations?.[language]?.helpText ?? q.helpText
}

export function optionLabel(o: SurveyQuestionOptionDto, language: string): string {
  return o.translations?.[language] ?? o.label
}

/** The consent script for the chosen language, or null when there is none in it. */
export function consentScript(bundle: SurveyBundleDto, language: string) {
  const scripts = bundle.instrument.consentScripts
  return scripts.find((s) => s.languageCode === language) ?? null
}

// ── completeness ────────────────────────────────────────────────────────────

export interface Completeness {
  answered: number
  required: number
  missingRequired: SurveyQuestionDto[]
  /** True when a COMPLETE disposition is honest. */
  complete: boolean
  audioMissing: boolean
}

export function completeness(): Completeness {
  const { visible, all, hiddenBy } = walk()
  const mode = snapshot.bundle?.instrument.audioRecordingMode ?? 'OFF'
  const asked: SurveyQuestionDto[] = []
  for (const q of visible) {
    asked.push(q)
    // A matrix row hidden by a branch was never asked, so it is not a required
    // question left blank — counting it would make every such interview PARTIAL.
    if (q.typeCode === 'MATRIX') {
      asked.push(...subQuestions(all, q).filter((r) => !hiddenBy[r.id]))
    }
  }
  const answered = asked.filter((q) => isAnswered(snapshot.answers[q.id])).length
  const missingRequired = asked.filter(
    (q) => q.required && !isAnswered(snapshot.answers[q.id]),
  )
  const audioMissing = mode === 'REQUIRED' && !hasClip('AUDIO_INTERVIEW')
  return {
    answered,
    required: asked.filter((q) => q.required).length,
    missingRequired,
    complete: missingRequired.length === 0 && !audioMissing,
    audioMissing,
  }
}

export function isAnswered(a: DraftAnswer | undefined): boolean {
  if (!a) return false
  if (a.optionCodes && a.optionCodes.length > 0) return true
  if (a.bool !== undefined) return true
  if (a.number !== undefined && a.number !== '') return true
  if (a.text !== undefined && a.text.trim() !== '') return true
  return false
}

// ── submitting ──────────────────────────────────────────────────────────────

/**
 * Turn the draft into a `CaptureRequests.ResponseSubmission`.
 *
 * Only questions that were ACTUALLY ANSWERED get a row. A question hidden by a
 * branch and a question shown and left empty are different facts, and neither is
 * expressible in `AnswerSubmission` — there is no "skipped" field — so the client
 * sends neither and the server's evaluator, which is the authoritative one,
 * marks each row `answered: false` with or without a `skippedByRuleId`. Sending
 * an empty row for a branched-past question would record it as shown-and-blank,
 * which is the instrument failing rather than the instrument working.
 */
function buildBody(dispositionCode: string): Record<string, unknown> {
  const s = snapshot
  const { all } = walk()
  const rows: Record<string, unknown>[] = []
  for (const q of all) {
    const a = s.answers[q.id]
    if (!isAnswered(a) || !a) continue
    rows.push({
      questionCode: q.code,
      optionCodes: a.optionCodes && a.optionCodes.length > 0 ? a.optionCodes : undefined,
      number: a.number !== undefined && a.number !== '' ? a.number : undefined,
      bool: a.bool,
      text: a.text !== undefined && a.text.trim() !== '' ? a.text : undefined,
      otherText: a.otherText !== undefined && a.otherText.trim() !== ''
        ? a.otherText : undefined,
    })
  }

  const startedAt = s.startedAt ?? new Date().toISOString()
  const place = s.place?.ok ? s.place.location : null
  const age = s.details.age.trim() === '' ? undefined : Number(s.details.age)

  return {
    idempotencyKey: s.idempotencyKey,
    assignmentId: s.assignmentId ?? undefined,
    unitId: s.details.unitId,
    languageCode: s.languageCode,
    respondentName: s.details.name.trim() || undefined,
    respondentPhone: s.details.phone.trim() || undefined,
    respondentPhoneCountry: s.details.phone.trim()
      ? (s.details.phoneCountry || undefined) : undefined,
    respondentNationalId: s.details.nationalId.trim() || undefined,
    identitySourceCode: s.details.identitySourceCode,
    // Only ever the id a LOCAL_REGISTER lookup gave us. The server ignores it on
    // any other source, which is the guard; this is the client not sending a
    // stale one after the interviewer edited the details by hand.
    matchedVoterId: s.details.identitySourceCode === 'LOCAL_REGISTER'
      ? s.details.matchedVoterId ?? undefined : undefined,
    respondentGender: s.details.gender || undefined,
    respondentAge: age !== undefined && Number.isFinite(age) ? Math.round(age) : undefined,
    consentGiven: s.consentGiven === true,
    consentAt: s.consentAt ?? undefined,
    consentMethodCode: s.consentMethodCode,
    // Sent exactly as recorded. The server derives the true duration from when
    // the submission arrives and flags a disagreement over 25%; a client that
    // "corrects" either number is defeating the check rather than passing it.
    startedAt,
    clientDurationSeconds: Math.max(
      0, Math.round((Date.now() - new Date(startedAt).getTime()) / 1000),
    ),
    latitude: place?.latitude,
    longitude: place?.longitude,
    geoAccuracyM: place?.accuracyM ?? undefined,
    geoCapturedAt: place?.capturedAt,
    channelCode: 'FIELD_APP',
    offline: typeof navigator !== 'undefined' && !navigator.onLine,
    optionOrderSeed: s.optionOrderSeed,
    dispositionCode,
    answers: rows,
  }
}

export function plannedDisposition(): string {
  if (snapshot.consentGiven === false) return 'REFUSAL'
  return completeness().complete ? 'COMPLETE' : 'PARTIAL'
}

/**
 * Send it — or queue it, which is also sending it, later.
 *
 * The split is exact and it matters. **Offline, or a network error**: the server
 * never saw this, so it goes in the queue and drains on `online`. **A 4xx**: the
 * server read it and said no, and the same bytes will get the same answer, so it
 * is rendered as a refusal with the server's own sentence and the interviewer
 * decides what to do. Queueing a refusal would mean an interviewer watching a
 * counter that never goes down.
 *
 * The key does not change between those attempts. That is what makes a retry
 * safe: a repeated key returns the ORIGINAL interview rather than a 409, so the
 * worst case for pressing Submit twice is hearing about the same interview
 * twice.
 */
export async function submit(dispositionOverride?: string): Promise<void> {
  const s = snapshot
  if (!s.surveyId || s.submitting) return
  /*
    THE RECORDING ENDS WITH THE INTERVIEW.

    This was missing and it cost more than a microphone light. `stopRecording` is
    what turns the running `MediaRecorder` into a clip in the store; until it
    runs the bytes are chunks the recorder is still holding, so
    `takeUnstoredClips` (offline) and `uploadHeldClips` (online) both found
    nothing and EVERY interview submitted without a manual stop produced no
    audio at all. The tab also kept the microphone open into the next interview.

    Awaited, and awaited HERE rather than after the POST: the clip has to exist
    before either path looks for it.

    It is not resumed if the server refuses the submission. The interview is
    over — what happens next is the interviewer correcting a field on a summary
    screen, and recording that is capturing a person's kitchen rather than an
    interview.
  */
  await stopRecording()
  const disposition = dispositionOverride ?? plannedDisposition()
  const body = buildBody(disposition)
  const attempts = s.attempts + 1
  set({ phase: 'submitting', submitting: true, refusal: null, refusalCode: null, attempts })

  const offline = typeof navigator !== 'undefined' && !navigator.onLine
  if (offline) {
    await queueOffline(body, 'This device was offline when the interview was submitted.')
    return
  }

  try {
    const detail = await submitResponse(s.surveyId, body)
    set({
      phase: 'done',
      submitting: false,
      queued: false,
      result: {
        responseId: detail.response.id,
        detail,
        retried: attempts > 1,
        dispositionCode: disposition,
      },
    })
    clearDraft(s.surveyId, s.assignmentId)
    // The response exists now, so the clips finally have something to belong to.
    // Deliberately not awaited: the interview is saved either way, and holding
    // the summary screen behind a 20 MB upload on a bad connection is how an
    // interviewer decides the app has hung and reloads it.
    void uploadHeldClips(detail.response.id)
    void drainQueue()
  } catch (e) {
    if (e instanceof ApiError) {
      set({
        // A refused REFUSAL goes to the summary rather than back to the details
        // form: there is nothing on that form to change, and the server's
        // sentence names an action — log a REFUSAL contact attempt — that the
        // summary can actually offer.
        phase: disposition === 'REFUSAL' ? 'done' : 'details',
        submitting: false,
        refusal: explain(e),
        refusalCode: e.code || null,
      })
      return
    }
    // Not an `ApiError` — `fetch` itself rejected, so nothing reached the
    // server. This is the case the queue exists for.
    await queueOffline(body, explain(e))
  }
}

async function queueOffline(body: Record<string, unknown>, reason: string): Promise<void> {
  const s = snapshot
  if (!s.surveyId) return
  // Taken out of the recorder and into IndexedDB under the same key: the clips
  // cannot be uploaded until the queued response is created and has an id.
  const clips = takeUnstoredClips()
  await queueInterview(s.surveyId, s.bundle?.surveyRef ?? null, body, clips, reason)
  set({ phase: 'done', submitting: false, queued: true, result: null })
  clearDraft(s.surveyId, s.assignmentId)
}

/** Try the same interview again, with the same key. Never a new one. */
export async function retrySubmit(): Promise<void> {
  await submit()
}

/**
 * Start the next respondent.
 *
 * A NEW key and a NEW seed, because this genuinely is a different interview —
 * and the recorder is reset so the previous respondent's audio cannot be
 * attached to it.
 *
 * ## The clip that had not gone up yet is stashed FIRST
 *
 * `submit()` fires `uploadHeldClips` without awaiting it, so a clip whose PUT
 * failed is sitting in the recorder store while the summary screen says "Upload
 * failed — it will be retried" directly above this button. `resetRecorder()`
 * clears that store. Without this the sentence was false and the bytes were gone
 * — the offline path stashes them and this one did not.
 */
export function startNextInterview(): void {
  const { surveyId, assignmentId, bundle, register, languageCode } = snapshot
  const responseId = snapshot.result?.responseId ?? null
  const finishedKey = snapshot.idempotencyKey
  run += 1
  if (responseId) {
    // A clip mid-`uploading` is taken too. It can cost a second media row if that
    // upload then succeeds, and a duplicate recording a coder can see is a far
    // smaller loss than a recording nobody has.
    const unsent = takeUnstoredClips()
    if (unsent.length > 0) {
      void holdClipsForUpload(finishedKey, responseId, bundle?.surveyRef ?? null, unsent)
    }
  }
  resetRecorder()
  if (surveyId) clearDraft(surveyId, assignmentId)
  set({
    ...blank(),
    surveyId,
    assignmentId,
    bundle,
    register,
    languageCode,
    fromCache: snapshot.fromCache,
    cachedAt: snapshot.cachedAt,
    consentRequired: bundle?.instrument.consentRequired ?? false,
    idempotencyKey: newKey(),
    optionOrderSeed: newSeed(),
    details: { ...BLANK_DETAILS, unitId: snapshot.details.unitId, unitName: snapshot.details.unitName },
  })
  if (snapshot.consentRequired) set({ phase: 'consent' })
  else beginQuestions()

  /*
    EACH INTERVIEW HAS ITS OWN RECORDING, INCLUDING THIS ONE.

    `resetRecorder()` above cancels the previous respondent's clip, which is
    right — but nothing restarted one, so recording only ever happened for the
    FIRST interview after the capture screen was opened. An interviewer working
    a street recorded door 1 and nothing after it, and the screen gave no sign
    of the difference.

    Not awaited: `startNextInterview` is called from a click handler and from
    `abandon`, and neither can be made async without the next door waiting on a
    microphone permission prompt. The recorder renders its own state.
  */
  if ((snapshot.bundle?.instrument.audioRecordingMode ?? 'OFF') !== 'OFF') {
    void startRecording('AUDIO_INTERVIEW')
  }
}

/**
 * Abandon this interview without submitting anything.
 *
 * It rolls straight into a fresh one rather than emptying the screen: the
 * interviewer is standing at the next door, and a blank page with no way back
 * would be a dead end that only a reload clears. The draft and the recording are
 * discarded first, so nothing from the abandoned respondent can attach itself to
 * the next.
 */
export function abandon(): void {
  const { surveyId, assignmentId } = snapshot
  if (surveyId) clearDraft(surveyId, assignmentId)
  // Emptied before `startNextInterview` can stash anything: an abandoned
  // interview's audio is meant to be discarded, and keeping it would leave a
  // recording of a respondent whose interview was deliberately thrown away
  // uploading itself against nothing.
  resetRecorder()
  startNextInterview()
}

/** Whether a clip exists, for the REQUIRED gate. Read through, not duplicated. */
export function recordingState() {
  return getRecorderSnapshot()
}
